import posthog from 'posthog-js'

import authStore from '@/features/auth/stores/auth/auth.store'
import type { V2AccountDataType } from '@/features/auth/types'
import programsStore from '@/features/programs/stores/programs/programs.store'
import type { V2ProgramType } from '@/features/programs/types'
import { I18N_DEFAULT_LOCALE, I18N_LOCAL_STORAGE_KEY } from '@/utils/config/constants'

export const PosthogClient = posthog

// analytics events
// naming convention: category:object_action
//posthog.com/product-engineers/5-ways-to-improve-analytics-data#1-implement-a-naming-convention
export const ANALYTICS_EVENTS = {
  /**
   * Triggered when the user completes all mandatory steps of the session review and clicks submit
   */
  USER_CLICK_COMPLETE_SESSION_REVIEW: 'sessions:complete_review_click',

  /**
   * Triggered when the user clicks on "Fill Later" in the session review modal
   * @property {string} step - The id of the current step on which the user clicked "Fill Later"
   */
  USER_CLICK_SESSION_REVIEW_FILL_LATER: 'sessions:review_step_fill_later_click',

  /**
   * Triggered when the automatic matching criteria modal is displayed (it now
   * opens directly on the fields). Lets us tell "never saw the popup" apart
   * from "saw it and left" — the two were indistinguishable in the Assas
   * diagnostic of 2026-09-21.
   * @property {boolean} mandatory - all fields required (mentor, or mentee without auto-match)
   * @property {number} missing_count - fields still empty when displayed
   * @property {number} total_count - fields in the template
   */
  MATCHING_CRITERIA_MODAL_VIEW: 'matching:criteria_modal_view',
  /**
   * Triggered when the matching criteria modal is left without a successful
   * submit — at most once per display for definitive exits. tab_hidden does
   * not lock: it may be followed by page_unload, another exit, or a submit.
   * Sent with sendBeacon so it survives a closed tab on mobile, where
   * $pageleave is unreliable.
   * @property {string} exit_method - tab_hidden | page_unload | support_click | program_switch | fill_later | navigation
   * @property {string|null} last_input_id - uuid of the last criteria field focused, null if none
   * @property {number} filled_count - fields filled when leaving (draft included)
   * @property {number} missing_count - fields still empty when leaving
   * @property {number} time_in_modal_ms - time since the modal was displayed
   */
  MATCHING_CRITERIA_MODAL_ABANDON: 'matching:criteria_modal_abandon',
  /** Triggered when mentor/mentee completes matching criteria */
  MATCHING_CRITERIA_COMPLETE: 'matching:criteria_complete',
  /** Triggered when mentee sees matching results in the slider */
  MATCHING_RESULTS_VIEW: 'matching:results_view',
  /** Triggered when mentee clicks a LinkedIn link in mentor description */
  MATCHING_LINKEDIN_CLICK: 'matching:linkedin_click',
  /** Triggered when mentee confirms mentor selection */
  MATCHING_MENTOR_SELECT: 'matching:mentor_select',
  /** Triggered when mentee closes modal without selecting a mentor */
  MATCHING_SELECT_ABANDON: 'matching:select_abandon',
  /** Triggered when mentee clicks "Change research" to restart matching */
  MATCHING_SEARCH_RESTART: 'matching:search_restart',
  /** Triggered when admin uses matching override (AI suggestion or manual) */
  MATCHING_ADMIN_OVERRIDE: 'matching:admin_override',
  /** Triggered when admin removes a user from a group (potential rematch) */
  MATCHING_REMATCH_REQUEST: 'matching:rematch_request',

  /**
   * Triggered on mount of the login page (top of the auth funnel — visitors
   * who never submit are invisible in the DB login_history).
   * @property {boolean} has_sso_option - SAML login is offered on this host
   * @property {string|null} program_slug - Tenant identifier (no PII)
   * @property {boolean} had_program_deep_link - Volet C: a valid ?programId= UUID was present in the entry URL
   */
  AUTH_LOGIN_START: 'auth:login_start',

  /**
   * Triggered on a failed login submission. error_type stays generic: the API
   * returns the same message for user_not_found / incorrect_password
   * (anti-enumeration v1.74) — the precise reason lives in DB (errorReason).
   * @property {'email'|'sso'} auth_method
   * @property {'invalid_credentials'|'sso_error'|'rate_limited'|'other'} error_type
   * @property {number} attempt_number - 1-based attempt count in this browser session
   * @property {number} time_since_start_ms - ms since the login page mounted
   * @property {'email'|'password'|'both'|'none'|null} field_changed_since_last - what the user edited since the previous attempt
   */
  AUTH_LOGIN_FAILURE: 'auth:login_failure',

  /**
   * Triggered when the MFA modal is shown (measures MFA abandon — mfa_attempts
   * only logs verifications, not the prompt).
   * @property {number} time_since_start_ms
   * @property {number} failed_attempts_before - login failures before reaching MFA
   */
  AUTH_MFA_PROMPT: 'auth:mfa_prompt',

  /**
   * Triggered on an invalid MFA code submission.
   * @property {number} mfa_attempt_number - 1-based MFA attempt count
   * @property {number} time_since_start_ms
   */
  AUTH_MFA_FAILURE: 'auth:mfa_failure',

  /**
   * Triggered on SSO intent, right before redirecting to the provider.
   * loginStartTimestamp is persisted to sessionStorage so timings survive the
   * provider round-trip.
   * @property {string|null} program_slug
   * @property {number} time_since_start_ms
   */
  /**
   * Triggered once the two-step login page has resolved which method applies to
   * the address typed on step 1. Measures how many people land on `not_found`
   * (wrong address) versus `sso` / `password`.
   * @property {'sso'|'password'|'not_found'|null} method
   * @property {number} time_since_start_ms - ms since the login page mounted
   */
  AUTH_LOGIN_METHOD_RESOLVED: 'auth:login_method_resolved',

  AUTH_SSO_REDIRECT: 'auth:sso_redirect',

  /**
   * Triggered when the user clicks "Forgot password" (frustration signal).
   * @property {number} failed_attempts_before
   * @property {number} time_since_start_ms
   */
  AUTH_PASSWORD_RESET_CLICK: 'auth:password_reset_click',

  /**
   * Triggered on a successful login (UX summary of the auth session).
   * @property {'email'|'email_mfa'|'sso'} auth_method
   * @property {number} time_to_login_ms
   * @property {number} failed_attempts_in_session
   * @property {boolean} used_password_reset
   * @property {boolean} is_first_login
   * @property {string|null} program_id
   * @property {string|null} role
   * @property {boolean} had_program_deep_link - Volet C
   * @property {boolean} program_context_preserved - Volet C: the initial deep-link programId is still the selected program after auth (baseline ~false before KC-501)
   */
  AUTH_LOGIN_COMPLETE: 'auth:login_complete',
}

export function initPosthog() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-expect-error
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string
  if (!apiKey) {
    return
  }
  // posthog only works when the key is set (could be a dev or prod key)
  PosthogClient.init(apiKey, {
    api_host: 'https://eu.i.posthog.com',
    person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
  })
}

/**
 * Locale actually displayed to the user, resolved in the same order as the guard in
 * `router.js`: the account language wins over local storage, which wins over the browser.
 * Kept in sync by hand rather than imported, because `@/lib/i18n` imports this module to
 * refresh the property and the reverse import would create a cycle. Reporting a locale
 * other than the one on screen is the very bug this property exists to fix.
 */
function getCurrentLocale(): string {
  return (
    authStore.userLanguage?.toLowerCase() ??
    window.localStorage.getItem(I18N_LOCAL_STORAGE_KEY) ??
    navigator.language?.split('-')[0] ??
    I18N_DEFAULT_LOCALE
  )
}

/**
 * Keeps the `language` person property in sync when the user switches the UI language.
 * PostHog resolves a survey translation from this property before falling back to the
 * browser locale, which is not the language the user picked in the platform.
 *
 * Only runs once the visitor has been identified: `setPersonProperties` on an anonymous
 * visitor forces PostHog to create a person profile for them, which defeats
 * `person_profiles: 'identified_only'` (one ghost profile per visit of the login page,
 * anonymous events billed as identified). The router guard calls this on every navigation,
 * logged in or not. On login, `identifyUser` already sends the language.
 */
export function setUserLanguage(locale: string) {
  if (PosthogClient.get_property('$user_state') !== 'identified') {
    return
  }
  PosthogClient.setPersonProperties({ language: locale })
}

export function identifyUser(account: Partial<V2AccountDataType>, programs: V2ProgramType[]) {
  // posthog-js 1.431 types identify() with a required string: an account without id
  // could never be identified anyway (identify(undefined) is a no-op upstream).
  if (!account.id) {
    return
  }
  PosthogClient.identify(account.id, {
    // roles: account.roles,
    // current_program_id is changed to the selected program id that the user is currently on
    current_program_id: programsStore.selectedProgramId,
    programs_ids: programs.map((program) => program.id).join(','),
    // language drives which survey translation PostHog shows to this user
    language: getCurrentLocale(),
  })
}

export function trackEvent(
  event: string,
  properties: Record<string, any> = {},
  options?: { transport?: 'XHR' | 'fetch' | 'sendBeacon' },
) {
  PosthogClient.capture(event, properties, options)
}
